import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link as LinkIcon, Plus, ShieldCheck, Target, X } from "lucide-react";
import { AxiosError } from "axios";

import { Heading, Text } from "@/components/Typography";
import { cn } from "@/utils/cn";
import { useToast } from "@/hooks/useToast";
import { api, type ReferralOut } from "@/services/api";
import { REFERRALS_TARGET, REFERRALS_TO_COMPLETE } from "@/constants/gettingStarted";
import { InviteLinkBar } from "@/pages/programs/ProgramsShared";

/**
 * "Refer three achievers" / "Refer three XOs".
 *
 * Built to the design prototype's `ReferralModal` (.rf-* rules), with two copy
 * changes that the design cannot keep: it says "Intro sent" and "Send referral
 * invite", but **the platform sends nothing**. The expert shares their link
 * themselves, so the list says "Referred" and the button says "Add referral".
 * Claiming an email went out when none did would be a lie told in the UI.
 *
 * THE LINK IS GENERIC — one per expert, no role in it. Whoever follows it
 * chooses achiever or XO at signup, and the admin's split follows that choice
 * rather than which link they happened to be sent.
 */

export type ReferralKind = "achiever" | "xo";

/** The prototype's rules, ported verbatim. The server enforces the same ones;
 *  these exist to tell the user before they submit. */
const emailOk = (e: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e.trim());
const liOk = (l: string) => !l.trim() || /linkedin\.com\//i.test(l);

/** Pre-filled into the LinkedIn field so the user only types the rest. Left
 *  as just this, the optional field counts as empty. */
const LINKEDIN_PREFIX = "https://www.linkedin.com/";
const liValue = (l: string) => (l.trim() === LINKEDIN_PREFIX ? "" : l.trim());

const COPY: Record<ReferralKind, { title: string; subtitle: string; cta: string }> = {
  achiever: {
    title: "Refer an achiever",
    subtitle: "A founder or operator stuck on a specific goal.",
    cta: "Refer an achiever",
  },
  xo: {
    title: "Refer an Expert Operator",
    subtitle: "Ten-plus years in one lane, already advising informally.",
    cta: "Refer an Expert Operator",
  },
};

const emptyForm = { name: "", email: "", linkedin: LINKEDIN_PREFIX };

export const ReferralModal = ({
  open,
  kind,
  onOpenChange,
  showProgress = true,
}: {
  open: boolean;
  kind: ReferralKind;
  onOpenChange: (open: boolean) => void;
  /**
   * The "N of 3 referred" count belongs to the Getting Started refer step.
   * Off for the general Invite a Colleague entry point, which is just an
   * invite: no count, no step messages, and it stays open after each add.
   */
  showProgress?: boolean;
}) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  // Set when Add is pressed on an incomplete form, so its errors show then —
  // the button stays enabled after each referral clears the form, rather than
  // greying out and reading as "you're finished".
  const [attempted, setAttempted] = useState(false);

  const { data } = useQuery({
    queryKey: ["getting-started-referrals"],
    queryFn: () => api.gettingStarted.getReferrals(),
    enabled: open,
    staleTime: 60 * 1000,
  });

  const list: ReferralOut[] = (kind === "xo" ? data?.xos : data?.achievers) ?? [];
  // Achiever referrals get the achiever signup link, XOs the Expert one.
  const shareLink = kind === "xo" ? data?.xoShareLink : data?.achieverShareLink;
  // The step is already ticked at one — the footer says Done from then on,
  // even though the dots still count to three.
  const done = showProgress && list.length >= REFERRALS_TO_COMPLETE;

  const add = useMutation({
    mutationFn: () =>
      api.gettingStarted.addReferral({
        kind: kind === "xo" ? "XO" : "ACHIEVER",
        name: form.name.trim(),
        email: form.email.trim(),
        linkedinUrl: liValue(form.linkedin) || undefined,
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(["getting-started-referrals"], next);
      const count = (kind === "xo" ? next.xos : next.achievers).length;
      // The API emails the person their signup link as it saves the referral.
      const invited = `Invite sent to ${form.email.trim()}`;
      setForm(emptyForm);
      setAttempted(false);
      if (!showProgress) {
        showToast({ type: "success", message: invited });
      } else if (count >= REFERRALS_TARGET) {
        // The full ask is in. Nothing left to add, so close.
        showToast({
          type: "success",
          message: `${invited} · ${kind === "xo" ? "three Expert Operators referred" : "three achievers referred"}`,
        });
        onOpenChange(false);
      } else if (count === REFERRALS_TO_COMPLETE) {
        // Stay open: the step is done, but they may well add the other two.
        showToast({ type: "success", message: `${invited} · step complete — add more if you can` });
      } else {
        showToast({ type: "success", message: `${invited} · ${count} of ${REFERRALS_TARGET}` });
      }
    },
    onError: (err) => {
      const detail =
        err instanceof AxiosError ? (err.response?.data?.detail as string | undefined) : undefined;
      showToast({ type: "error", message: detail || "Could not add that referral." });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.gettingStarted.removeReferral(id),
    onSuccess: (next) => queryClient.setQueryData(["getting-started-referrals"], next),
  });

  const nameErr = attempted && !form.name.trim();
  const emailErr = (attempted || !!form.email.trim()) && !emailOk(form.email);
  const liErr = !liOk(form.linkedin);
  const valid = !!form.name.trim() && emailOk(form.email) && liOk(form.linkedin);

  const Icon = kind === "xo" ? ShieldCheck : Target;
  const copy = COPY[kind];

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) setAttempted(false);
        onOpenChange(next);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
        <Dialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 flex w-full -translate-x-1/2 -translate-y-1/2 flex-col",
            "max-h-[calc(100vh-48px)] max-w-[min(520px,calc(100vw-32px))] overflow-hidden",
            "rounded-[18px] border border-theme-border bg-theme-bg-surface shadow-xl focus:outline-none",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          )}
        >
          {/* Header */}
          <div className="flex items-start gap-3 px-6 pb-4 pt-[22px]">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-theme-accent-subtle text-primary-purple">
              <Icon size={17} />
            </span>
            <div className="min-w-0 flex-1">
              <Dialog.Title asChild>
                <Heading
                  as="h3"
                  className="text-[17px] font-bold tracking-[-0.2px] text-theme-text-heading"
                >
                  {copy.title}
                </Heading>
              </Dialog.Title>
              <Dialog.Description asChild>
                <Text as="p" className="mt-1 text-[12.5px] leading-[1.5] text-theme-text-secondary">
                  {copy.subtitle}
                </Text>
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                aria-label="Close"
                className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full bg-theme-accent-subtle text-theme-text-secondary transition-colors hover:text-theme-text-heading"
              >
                <X size={15} />
              </button>
            </Dialog.Close>
          </div>

          {/* Progress */}
          {showProgress && (
            <div className="flex items-center gap-2.5 px-6 pb-4 text-[12px] font-semibold text-theme-text-secondary">
              <div className="flex max-w-[160px] flex-1 gap-1">
                {Array.from({ length: REFERRALS_TARGET }).map((_, i) => (
                  <span
                    key={i}
                    className={cn(
                      "h-[5px] flex-1 rounded-full",
                      i < list.length ? "bg-success-500" : "bg-theme-border-subtle",
                    )}
                  />
                ))}
              </div>
              {Math.min(list.length, REFERRALS_TARGET)} of {REFERRALS_TARGET} referred
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto">
            {/* Share link — the thing they actually send. */}
            <div className="border-t border-theme-border px-6 py-4">
              <Text
                as="p"
                className="mb-2 text-[11px] font-semibold uppercase tracking-[0.4px] text-theme-text-muted"
              >
                Your referral link
              </Text>
              {shareLink ? (
                <InviteLinkBar link={shareLink} />
              ) : (
                <div className="h-10 animate-pulse rounded-lg bg-theme-bg-subtle" aria-hidden />
              )}
              <Text as="p" className="mt-2 text-[11.5px] leading-[1.5] text-theme-text-muted">
                Share this with whoever you refer. When they sign up through it, the referral is
                credited to you.
              </Text>
            </div>

            {/* Form */}
            <div className="flex flex-col gap-3 border-t border-theme-border px-6 py-[18px]">
              <label className="flex flex-col gap-1.5 text-[12px] font-medium text-theme-text-heading">
                Full name
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Jordan Lee"
                  className={cn(
                    "w-full rounded-[10px] border bg-theme-bg-surface px-3 py-2.5 text-[13.5px] text-theme-text-heading outline-none",
                    nameErr
                      ? "border-error-500"
                      : "border-theme-border focus:border-primary-purple",
                  )}
                />
                {nameErr && <span className="text-[11px] text-error-500">Enter their name</span>}
              </label>

              <label className="flex flex-col gap-1.5 text-[12px] font-medium text-theme-text-heading">
                Email
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="jordan@company.com"
                  className={cn(
                    "w-full rounded-[10px] border bg-theme-bg-surface px-3 py-2.5 text-[13.5px] text-theme-text-heading outline-none",
                    emailErr
                      ? "border-error-500"
                      : "border-theme-border focus:border-primary-purple",
                  )}
                />
                {/* Shown once something has been typed or Add was pressed, so
                    an untouched form is not scolded. */}
                {emailErr && (
                  <span className="text-[11px] text-error-500">Enter a valid email</span>
                )}
              </label>

              <label className="flex flex-col gap-1.5 text-[12px] font-medium text-theme-text-heading">
                <span>
                  LinkedIn URL <span className="font-normal text-theme-text-muted">· optional</span>
                </span>
                <input
                  value={form.linkedin}
                  onChange={(e) => setForm((f) => ({ ...f, linkedin: e.target.value }))}
                  placeholder="https://www.linkedin.com/in/jordanlee"
                  className={cn(
                    "w-full rounded-[10px] border bg-theme-bg-surface px-3 py-2.5 text-[13.5px] text-theme-text-heading outline-none",
                    liErr ? "border-error-500" : "border-theme-border focus:border-primary-purple",
                  )}
                />
                {liErr && (
                  <span className="text-[11px] text-error-500">
                    Use a linkedin.com profile link
                  </span>
                )}
              </label>
            </div>

            {/* Already referred */}
            {list.length > 0 && (
              <div className="flex flex-col gap-1.5 px-6 pb-3.5 pt-1">
                {list.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center gap-2.5 rounded-[10px] bg-theme-bg-subtle px-3 py-2.5"
                  >
                    <div className="min-w-0 flex-1">
                      <Text
                        as="p"
                        className="truncate text-[13px] font-semibold text-theme-text-heading"
                      >
                        {r.name}
                      </Text>
                      <Text as="p" className="truncate text-[11.5px] text-theme-text-secondary">
                        {r.email}
                      </Text>
                    </div>
                    {r.linkedinUrl && (
                      <a
                        href={r.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="LinkedIn"
                        className="flex-shrink-0 text-theme-text-secondary hover:text-primary-purple"
                      >
                        <LinkIcon size={14} />
                      </a>
                    )}
                    {/* "Referred", not the design's "Intro sent" — nothing was
                        sent by the platform. */}
                    <span
                      className={cn(
                        "flex-shrink-0 text-[11px] font-semibold",
                        r.converted ? "text-theme-text-success" : "text-theme-text-muted",
                      )}
                    >
                      {r.converted ? "Joined" : "Referred"}
                    </span>
                    <button
                      type="button"
                      aria-label={`Remove ${r.name}`}
                      onClick={() => remove.mutate(r.id)}
                      className="flex-shrink-0 p-0.5 text-theme-text-muted transition-colors hover:text-theme-text-heading"
                    >
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 border-t border-theme-border bg-theme-bg-subtle px-6 py-3.5">
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-[11px] border border-theme-border px-[15px] py-2.5 text-[12.5px] font-semibold text-theme-text-secondary transition-colors hover:bg-theme-bg-hover"
              >
                {done ? "Done" : "Close"}
              </button>
            </Dialog.Close>
            <button
              type="button"
              disabled={add.isPending}
              onClick={() => {
                if (!valid) {
                  setAttempted(true);
                  return;
                }
                add.mutate();
              }}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-[10px] bg-primary-purple px-4 py-2.5 text-[13px] font-semibold text-white transition-opacity",
                add.isPending ? "cursor-not-allowed opacity-45" : "hover:opacity-90",
              )}
            >
              <Plus size={13} /> Add referral
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
